// DebtsDataSection.jsx
import React from "react";
import {
  Info, CheckCircle2, AlertCircle,
  BadgeDollarSign, CircleDollarSign,
  ShieldAlert, FileWarning
} from "lucide-react";

/** --- Visual primitives (same style as other risk sections) --- */
const Karta = ({ children, style }) => (
  <div
    style={{
      border: "1px solid #e6e9ee",
      borderRadius: 16,
      padding: 16,
      background: "#fff",
      boxShadow: "0 16px 48px rgba(16,24,40,0.16), 0 8px 22px rgba(16,24,40,0.12)",
      ...style,
    }}
  >
    {children}
  </div>
);

const Sekcja = ({ title, icon: Icon = Info, children }) => (
  <Karta>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
      {Icon ? <Icon size={18} color="#0b74e5" /> : null}
      <h3
        style={{
          margin: 0,
          fontSize: 18,
          color: "#0b74e5",
          textDecorationThickness: "2px",
          textUnderlineOffset: 4,
        }}
      >
        {title}
      </h3>
    </div>
    {children}
  </Karta>
);

const StatusCard = ({ title, status, tone = "info", subtitle, icon: LeadIcon }) => {
  const tones = {
    ok:   { top: "#22c55e", ring: "#22c55e", text: "#111827", sub: "#6b7280", border: "#e6f6ec", Fallback: CheckCircle2 },
    bad:  { top: "#ef4444", ring: "#ef4444", text: "#111827", sub: "#6b7280", border: "#fdecec", Fallback: AlertCircle  },
    warn: { top: "#f59e0b", ring: "#f59e0b", text: "#111827", sub: "#6b7280", border: "#fef3c7", Fallback: AlertCircle  },
    info: { top: "#3b82f6", ring: "#3b82f6", text: "#111827", sub: "#6b7280", border: "#dbeafe", Fallback: Info        },
  };
  const c = tones[tone] || tones.info;
  const IconComp = LeadIcon || c.Fallback;

  return (
    <div
      style={{
        border: `1px solid ${c.border}`,
        borderRadius: 14,
        background: "#fff",
        boxShadow: "0 6px 24px rgba(16,24,40,0.06)",
        overflow: "hidden",
        display: "grid",
      }}
    >
      <div style={{ height: 6, background: c.top }} />
      <div style={{ display: "grid", gridTemplateColumns: "56px 1fr", gap: 12, padding: 14 }}>
        <div
          style={{
            display: "grid",
            placeItems: "center",
            height: 56,
            width: 56,
            borderRadius: "999px",
            border: `2px solid ${c.ring}`,
            color: c.ring,
          }}
        >
          <IconComp size={28} />
        </div>
        <div style={{ display: "grid", gap: 6 }}>
          <div style={{ fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase", color: "#6b7280", fontWeight: 700 }}>
            {title}
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: c.text }}>{status}</div>
          {subtitle ? <div style={{ fontSize: 12, color: c.sub }}>{subtitle}</div> : null}
        </div>
      </div>
    </div>
  );
};

/** --- Helpers --- */
const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : s);

const pln = (v) => {
  if (v == null || v === "" || v === "-") return "—";
  const n = Number(String(v).replace(/\s/g, "").replace(",", "."));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("pl-PL", { style: "currency", currency: "PLN", maximumFractionDigits: 2 });
};

function yesNoTone(raw) {
  const v = String(raw ?? "").trim().toLowerCase();
  if (v === "tak") return { tone: "bad", display: "Tak" }; // obecność długów/ryzyk = negatywna
  if (v === "nie") return { tone: "ok", display: "Nie" };
  if (v === "nie dotyczy") return { tone: "info", display: "Nie dotyczy" };
  return { tone: "info", display: cap(raw) || "—" };
}

/** Icon map per field */
const iconMap = {
  private_debts: BadgeDollarSign,        // Giełdy wierzytelności (Tak/Nie)
  private_debts_amount: CircleDollarSign,// Suma wierzytelności
  rdn: ShieldAlert,                      // Rejestr Dłużników Niewypłacalnych
  krs_debts: FileWarning,                // Zaległości w Dziale IV KRS
};

/** --- Component --- */
export default function DebtsDataSection({ data = {} }) {
  // Expect: { private_debts, private_debts_amount, rdn, krs_debts }
  const get = (k) => data?.[k]?.value ?? null;
  const label = (k) => data?.[k]?.label_short ?? k;
  const desc = (k) => data?.[k]?.label_long ?? "";

  const amountRaw = get("private_debts_amount");
  const amountNum = Number(String(amountRaw ?? "").replace(/\s/g, "").replace(",", "."));
  const amountTone = Number.isFinite(amountNum) ? (amountNum > 0 ? "bad" : "ok") : "info";

  const cards = [
    (() => {
      const v = get("private_debts");
      const { tone, display } = yesNoTone(v);
      return {
        key: "private_debts",
        icon: iconMap.private_debts,
        title: label("private_debts"),
        status: display,
        tone,
        subtitle: desc("private_debts"),
      };
    })(),
    {
      key: "private_debts_amount",
      icon: iconMap.private_debts_amount,
      title: label("private_debts_amount"),
      status: pln(amountRaw),
      tone: amountTone,
      subtitle: desc("private_debts_amount"),
    },
    (() => {
      const v = get("rdn");
      const { tone, display } = yesNoTone(v);
      return {
        key: "rdn",
        icon: iconMap.rdn,
        title: label("rdn"),
        status: display,
        tone,
        subtitle: desc("rdn"),
      };
    })(),
    (() => {
      const v = get("krs_debts");
      const { tone, display } = yesNoTone(v);
      return {
        key: "krs_debts",
        icon: iconMap.krs_debts,
        title: label("krs_debts"),
        status: display,
        tone,
        subtitle: desc("krs_debts"),
      };
    })(),
  ];

  const hasAny = cards.some((c) => c);

  if (!hasAny) {
    return (
      <Sekcja title="Zadłużenia">
        <em>Brak danych.</em>
      </Sekcja>
    );
  }

  return (
   <Sekcja title="Zadłużenia" icon={BadgeDollarSign}>
      <style>{`
        .debtsGrid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 16px;
        }
        @media (max-width: 1280px) { .debtsGrid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
        @media (max-width: 1024px) { .debtsGrid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        @media (max-width: 640px)  { .debtsGrid { grid-template-columns: 1fr; } }
      `}</style>

      <div className="debtsGrid">
        {cards.map((c) => (
          <StatusCard
            key={c.key}
            title={c.title}
            status={c.status}
            tone={c.tone}
            subtitle={c.subtitle}
            icon={c.icon}
          />
        ))}
      </div>
    </Sekcja>
  );
}
